import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { FieldValues } from 'react-hook-form';
import { z } from 'zod';

import { choiceOptions, defineEnum } from '@/shared/patterns';

import { EntityPickerField } from './entity-picker';
import type { EntitySource } from './entity-source';
import { SelectFormField, TextFormField } from './fields';

/** Tipos de conta do legado (`FundSchema`, `BankDetails`), pelo valor técnico (U33). */
export const BANK_ACCOUNT_TYPES = defineEnum({
  NaturalCheckingAccount: { label: msg`Conta Corrente de Pessoa Física` },
  NaturalSavingsAccount: { label: msg`Poupança de Pessoa Física` },
  NaturalSimpleAccount: { label: msg`Conta Simples de Pessoa Física` },
  LegalCheckingAccount: { label: msg`Conta Corrente de Pessoa Jurídica` },
  LegalSavingsAccount: { label: msg`Poupança de Pessoa Jurídica` },
});

/** Conta no formulário (modo TED com dígitos): tudo texto; o mapper converte para o fio. */
export interface BankAccountValues {
  type: string;
  bankCode: string;
  bankCodeDisplay?: string | null | undefined;
  agency: string;
  agencyDigit: string;
  account: string;
  accountDigit: string;
  [field: string]: unknown;
}

export const BANK_ACCOUNT_EMPTY: BankAccountValues = { type: '', bankCode: '', bankCodeDisplay: null, agency: '', agencyDigit: '', account: '', accountDigit: '' };

export interface BankAccountOptions {
  /** `allOrNothing` (Cessionários: vazio vai `null`; um campo preenchido exige os outros) ou `all`. */
  readonly required?: 'all' | 'allOrNothing';
}

const text = z.preprocess((value) => (value === null || value === undefined ? '' : typeof value === 'number' ? String(value) : value), z.string());
const EDITABLE = ['type', 'bankCode', 'agency', 'agencyDigit', 'account', 'accountDigit'] as const;

/** A conta está em branco (os seis campos): no "tudo ou nada", vai `null` ao fio. */
export function isBankAccountBlank(value: Partial<BankAccountValues> | null | undefined): boolean {
  return !value || EDITABLE.every((key) => (value[key] ?? '').trim() === '');
}

/** Schema da conta (P15, modo TED com dígitos), com as mensagens do legado (U20-5 na caixa e na grafia). */
export function bankAccountSchema(i18n: I18n, options: BankAccountOptions = {}): z.ZodType<BankAccountValues, BankAccountValues> {
  const mode = options.required ?? 'allOrNothing';
  const messages = {
    type: i18n._(msg`Tipo de conta: precisa ser preenchido.`),
    bankCode: i18n._(msg`Código bancário: precisa ser preenchido.`),
    agency: i18n._(msg`Agência: precisa ser preenchida.`),
    agencyMax: i18n._(msg`Agência: precisa ter no máximo 4 caracteres.`),
    agencyDigit: i18n._(msg`Dígito da agência: precisa ter no máximo 1 caractere.`),
    account: i18n._(msg`Conta: precisa ser preenchida.`),
    accountDigit: i18n._(msg`Dígito da conta: precisa ter no máximo 1 caractere.`),
    number: i18n._(msg`Valor precisa ser um número.`),
  };
  return z
    .looseObject({ type: text, bankCode: text, bankCodeDisplay: z.string().nullish(), agency: text, agencyDigit: text, account: text, accountDigit: text })
    .superRefine((value, context) => {
      const issue = (path: string, message: string) => context.addIssue({ code: 'custom', path: [path], message });
      if (mode === 'allOrNothing' && isBankAccountBlank(value)) return;
      for (const key of ['type', 'bankCode', 'agency', 'account'] as const) if (value[key].trim() === '') issue(key, messages[key]);
      const digits = (raw: string) => raw.trim() === '' || /^\d+$/.test(raw.trim());
      if (!digits(value.agency)) issue('agency', messages.number);
      else if (value.agency.trim().length > 4) issue('agency', messages.agencyMax);
      if (!digits(value.account)) issue('account', messages.number);
      if (!digits(value.agencyDigit)) issue('agencyDigit', messages.number);
      else if (value.agencyDigit.trim().length > 1) issue('agencyDigit', messages.agencyDigit);
      if (!digits(value.accountDigit)) issue('accountDigit', messages.number);
      else if (value.accountDigit.trim().length > 1) issue('accountDigit', messages.accountDigit);
    }) as unknown as z.ZodType<BankAccountValues, BankAccountValues>;
}

export interface BankAccountFieldsProps<T> {
  /** Onde a conta mora no formulário (`bankAccount`). */
  readonly prefix?: string;
  /** Bancos (o `GET /Bank?compe=true` do módulo): opção "<código> - <nome>". */
  readonly banks: EntitySource<T>;
  /** Mostra o `*` nos obrigatórios. No "tudo ou nada" o legado não mostrava (o erro aparece no Salvar). */
  readonly required?: boolean;
}

/** Campos de conta bancária (P15, modo TED com dígitos, 06 §3.d): tipo, banco, agência e conta. */
export function BankAccountFields<T>({ prefix = 'bankAccount', banks, required = false }: BankAccountFieldsProps<T>) {
  const { i18n, t } = useLingui();
  const path = (key: string) => (prefix ? `${prefix}.${key}` : key);
  return (
    <>
      <SelectFormField name={path('type')} label={t`Tipo da conta`} required={required} options={choiceOptions(i18n, BANK_ACCOUNT_TYPES)} className="lg:col-span-1" />
      <EntityPickerField<FieldValues, T> name={path('bankCode')} displayName={path('bankCodeDisplay')} label={t`Banco`} required={required} source={banks} placeholder={t`Buscar por código ou nome`} className="lg:col-span-2" />
      <TextFormField name={path('agency')} label={t`Agência`} required={required} inputMode="numeric" maxLength={4} />
      <TextFormField name={path('agencyDigit')} label={t`Dígito da agência`} inputMode="numeric" maxLength={1} />
      <span aria-hidden className="hidden lg:block" />
      <TextFormField name={path('account')} label={t`Conta`} required={required} inputMode="numeric" />
      <TextFormField name={path('accountDigit')} label={t`Dígito da conta`} inputMode="numeric" maxLength={1} />
    </>
  );
}
